'use client';

import { Add, Delete } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { DataGridPremium as DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium';
import { ApolloQueryResult, useMutation } from '@parkchirp/api-client';
import { adaptGridValueCallback } from '@parkchirp/ui-library';
import { LeaseSubtenantTypeDisplayEnum } from 'parkchirp-shared/enums';
import React, { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react';

import { DELETE_LEASE_SUBTENANT } from '../../../mutations';
import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { LeasingRight, Subtenant } from '../../../types/leases';
import { isAdminDashboard, isTenantPortal } from '../../../utils/constants';
import { SimpleModal } from '../../modals';
import { CreateSubtenantForm } from './createSubtenantForm';

type SubtenantsProps = {
	subtenants: Subtenant[];
	setSubtenants?: Dispatch<SetStateAction<Subtenant[]>>; // Used when leaseNewMode
	leasingRight: LeasingRight;
	refetchLeasingRight?: () => Promise<ApolloQueryResult<any>>; // Used when !leaseNewMode
	leaseNewMode?: boolean;
	setShowDialog: SetShowDialog;
	setShowSnackbar: SetShowSnackbar;
	getUrlToSubscriptionPage: (...x: any) => string;
};

const getColumns: (
	leasingRight: LeasingRight,
	handleDelete: (x: number) => Promise<void>,
	deleteInProgress: boolean,
	getUrlToSubscriptionPage: (...x: any) => string,
) => GridColDef[] = (leasingRight, handleDelete, deleteInProgress, getUrlToSubscriptionPage) => [
	{
		field: 'actions',
		type: 'actions' as const,
		getActions: ({ id }) => {
			if (deleteInProgress) return [<CircularProgress key='progress' />];

			return [
				<GridActionsCellItem
					icon={<Delete />}
					onClick={() => handleDelete(id as number)}
					label=''
					key='delete'
				/>,
			];
		},
		flex: 0.25,
	},
	{
		field: 'accountId',
		headerName: 'Acct. ID',
		headerAlign: 'center' as const,
		align: 'center' as const,
		flex: 1,
		resizable: false,
		sortable: false,
		renderCell: ({ row, value: accountId }) => {
			if (isTenantPortal) return `AC-${accountId}`;

			return (
				<a
					style={{ color: 'blue' }}
					target={window.parent.Cypress ? '_self' : '_blank'}
					href={getUrlToSubscriptionPage(row.subscriptionId, leasingRight.facilityId, accountId)}
					data-cy='account-id-link'
					rel='noreferrer'
				>
					AC-{accountId}
				</a>
			);
		},
	},
	{
		field: 'subscriptionName',
		headerName: 'Account Name',
		headerAlign: 'center' as const,
		align: 'center' as const,
		flex: 1,
		resizable: false,
		sortable: false,
	},
	{
		field: 'type',
		headerName: 'Type',
		headerAlign: 'center' as const,
		align: 'center' as const,
		flex: 1,
		resizable: false,
		sortable: false,
		valueFormatter: adaptGridValueCallback(({ value }) => LeaseSubtenantTypeDisplayEnum[value]),
	},
];

export const Subtenants: FC<SubtenantsProps> = ({
	subtenants,
	setSubtenants,
	leasingRight,
	refetchLeasingRight,
	leaseNewMode = false,
	setShowDialog,
	setShowSnackbar,
	getUrlToSubscriptionPage,
}) => {
	const [deleteLeaseSubtenant, { loading: deleteInProgress }] = useMutation(DELETE_LEASE_SUBTENANT);

	const handleDelete = useCallback(
		async (subtenantId: number) => {
			if (leaseNewMode) {
				setSubtenants((prev) => prev.filter(({ id }) => id !== subtenantId));
				return;
			}

			setShowDialog(
				<SimpleModal
					content='Are you sure?'
					handleDismiss={() => setShowDialog(null)}
					handleConfirm={async () => {
						setShowDialog(null);

						try {
							await deleteLeaseSubtenant({
								variables: {
									id: subtenantId,
									leasingRightId: leasingRight.id,
								},
								context: { apiVersion: 'v2' },
							});

							setShowSnackbar({
								message: 'Deleted successfully',
								severity: 'success',
								autoHideDuration: 5000,
							});

							await refetchLeasingRight();
						} catch (e) {
							console.log(e);
							setShowSnackbar({
								message: 'Internal Server Error',
								severity: 'error',
								autoHideDuration: 5000,
							});
						}
					}}
					confirmText='Delete'
					confirmButtonColor='error'
					dismissText='Cancel'
					contentStyle={{
						fontSize: 'large',
						display: 'flex',
						justifyContent: 'center',
						margin: '3em',
						minWidth: '20em',
					}}
				/>,
			);
		},
		[
			deleteLeaseSubtenant,
			leaseNewMode,
			refetchLeasingRight,
			setShowDialog,
			setShowSnackbar,
			setSubtenants,
			leasingRight.id,
		],
	);

	const columns = useMemo(
		() => getColumns(leasingRight, handleDelete, deleteInProgress, getUrlToSubscriptionPage),
		[deleteInProgress, getUrlToSubscriptionPage, handleDelete, leasingRight],
	);

	const renderCreateSubtenantForm = useCallback(() => {
		setShowDialog(
			<CreateSubtenantForm
				leasingRight={leasingRight}
				refetchLeasingRight={refetchLeasingRight}
				subtenants={subtenants}
				setSubtenants={setSubtenants}
				leaseNewMode={leaseNewMode}
				newSubtenantId={subtenants.length}
				setShowDialog={setShowDialog}
				setShowSnackbar={setShowSnackbar}
			/>,
		);
	}, [leaseNewMode, leasingRight, refetchLeasingRight, setShowDialog, setShowSnackbar, setSubtenants, subtenants]);

	return (
		<Box borderBottom='solid lightgray' paddingBottom='2em' mt='1.5em'>
			<Box width='50%'>
				<Box display='flex' flexDirection='row' gap='1em' mb='1em'>
					<Typography variant='h5' fontWeight='bold' alignContent='center'>
						Subtenants
					</Typography>
					{isAdminDashboard && (
						<Button variant='contained' startIcon={<Add />} onClick={renderCreateSubtenantForm}>
							Add
						</Button>
					)}
				</Box>
				{!!subtenants.length && (
					<DataGrid
						rows={subtenants}
						columns={columns}
						columnVisibilityModel={{ actions: !isTenantPortal }}
						disableColumnReorder
						disableColumnMenu
						hideFooter={subtenants.length < 6}
						autoHeight={subtenants.length < 6}
						sx={{ maxHeight: 400 }}
					/>
				)}
			</Box>
		</Box>
	);
};

export default Subtenants;
